import { GlobalState, useStateMachine } from 'little-state-machine';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { JOB_POST_EDITOR_DEFAULT_STATE } from '../../../../../utils/applicant_tracking/jobDetails';
import { useNavigate } from 'react-router';
import { CreateJobDto } from '../../../../../services/applicant_tracking/JobService/CreateJobDto';
import { buildUrl, replaceFinalSegment } from '../../../../../utils/url';
import { JobPostPageLayout } from './JobPostPageLayout';
import { JobAndJobPost, initCreateJobDto } from '..';
import { getReferrer } from '../referrers';
import { LocationInput, loadLocationOptions } from './LocationInput';
import { EmploymentTypeInput } from './EmploymentTypeInput';
import { SelectOption } from '../../../../../components/Select';
import { LoadingSpinner } from '../../../../../components/LoadingSpinner';
import { FormErrorMessage } from '../../../../../components/FormErrorMessage';
import { handleFormErrors } from '../../../../../services/ApiService/handler';
import { JobDetails } from '../../../../../services/applicant_tracking/JobService/EditJobDto';
import { ExperienceLevelSelect } from './ExperienceLevelSelect';
import { JobDescriptionInput } from './JobDescriptionInput';
import { Card } from '../../../../../components/Card';
import { WorkplaceInput } from './WorkplaceInput';

interface PropTypes {
  setupStep: number;
  setSetupStep: (newStep: number, jobDTO: CreateJobDto) => void;
  editorState?: string;
  updateLocalStorage: (state: GlobalState, payload: any) => GlobalState;
  initJobDto: CreateJobDto;
  isEditFlow: boolean;
  jobDetails: JobDetails;
  onSubmit: (state: CreateJobDto) => Promise<JobAndJobPost>;
}

export function SetupJobPostPage(props: PropTypes) {
  const navigate = useNavigate();
  const updateLocalStorage = props.updateLocalStorage;
  const { actions, state } = useStateMachine({ updateLocalStorage });
  const [createJobStore, setCreateJobStore] = useState<GlobalState>(state);

  const {
    handleSubmit,
    control,
    formState,
    reset,
    getValues,
    setValue,
    resetField,
    setError,
    watch,
    trigger,
  } = useForm<CreateJobDto>({
    mode: 'onChange',
    defaultValues: props.initJobDto,
  });

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [locationOptions, setLocationOptions] = useState<SelectOption[]>([]);
  const [editorTouched, setEditorTouched] = useState<boolean>(props.isEditFlow);

  useEffect(() => {
    (async () => {
      setLocationOptions(await loadLocationOptions());

      setIsLoading(false);
    })();
  }, []);

  useEffect(() => {
    reset(props.initJobDto);
  }, [props.initJobDto]);

  useEffect(() => {
    if (!isLoading && props.isEditFlow) {
      setValue(
        'editorState',
        getValues('editorState') ?? JOB_POST_EDITOR_DEFAULT_STATE,
      );
      trigger();
    }
  }, [isLoading]);

  const onSubmit = handleSubmit(async () => {
    const isSuccessful = await submitData();
    if (!isSuccessful) return;

    props.setSetupStep(props.setupStep + 1, state.setupJob);
    navigate(replaceFinalSegment('application_form'));
  });

  const submitData = async () => {
    setIsSaving(true);
    try {
      const newstate = { ...state.setupJob, ...getValues() };
      const { jobPost } = await props.onSubmit(newstate);

      actions.updateLocalStorage({ ...newstate, jobPostId: jobPost.id });

      return true;
    } catch (e: unknown) {
      handleFormErrors(e, setError);
      setIsSaving(false);
    }

    return false;
  };

  const onSave = handleSubmit(async () => {
    const isSuccessful = await submitData();
    if (!isSuccessful) return;

    window.location.href = getReferrer();
  });

  const onEditorChange = useCallback(() => {
    if (!editorTouched) setEditorTouched(true);
  }, [editorTouched]);

  const updateGlobalStateCallback = useCallback(
    (data: any) => {
      const updatedState = {
        ...createJobStore,
        setupJob: { ...createJobStore.setupJob, ...getValues(), ...data },
      };
      setCreateJobStore(updatedState);
      actions.updateLocalStorage(updatedState.setupJob);
    },
    [createJobStore],
  );

  const previewUrl = buildUrl(
    `${window.location.origin}/applicant_tracking/job_posts/preview`,
    [
      { key: 'name', value: watch('name') },
      { key: 'department_id', value: watch('departmentId') },
      { key: 'location_id', value: watch('locationId') },
      { key: 'location_type', value: watch('locationType') },
    ],
  );
  const onNavigate = handleSubmit(async () => {
    actions.updateLocalStorage({ ...getValues() });
  });

  return (
    <Card className={'px-4 pt-4 pb-5 mb-5'}>
      <form onSubmit={onSubmit}>
        <FormErrorMessage error={formState.errors} />
        <JobPostPageLayout
          formState={formState}
          jobDetails={props.jobDetails}
          setupStep={props.setupStep}
          setSetupStep={props.setSetupStep}
          onCancel={() => {
            actions.updateLocalStorage(initCreateJobDto);
            window.location.href = getReferrer();
          }}
          onSave={onSave}
          onSaveAndContinue={onSubmit}
          onNavigate={onNavigate}
          isEditFlow={props.isEditFlow}
          isLoading={isLoading}
          isSaving={isSaving}
          editorTouched={editorTouched}
        >
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              <WorkplaceInput
                formControl={control}
                formResetField={resetField}
              />
              <LocationInput
                formControl={control}
                options={locationOptions}
                setLocationOptions={setLocationOptions}
                setIsLoading={setIsLoading}
                setValue={setValue}
              />
              <EmploymentTypeInput
                formControl={control}
                formResetField={resetField}
              />
              <ExperienceLevelSelect formControl={control} />
              <JobDescriptionInput
                formControl={control}
                formState={formState}
                formResetField={resetField}
                formGetValues={getValues}
                formSetValue={setValue}
                previewUrl={previewUrl}
                updateGlobalStateCallback={updateGlobalStateCallback}
                onEditorChange={onEditorChange}
              />
            </>
          )}
        </JobPostPageLayout>
      </form>
    </Card>
  );
}
